import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Table,
  Badge,
  ProgressBar,
  Card,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
} from "recharts";
import { Kpi } from "../../Sales/Components/Kpi";
import { ChartCard } from "../../../components/dashboard/ChartCard";
import {
  STOCK_CATEGORIES,
  ADJUSTMENT_REASONS,
  monthlyStockValue,
  monthlyMovements,
  monthlyAdjustments,
  monthlyConsumption,
  topConsumedItems,
  reorderAlerts,
  stockValueByCategory,
  stockValueByStore,
  filterItemsMethod,
} from "../../../lib/mockStocks";
import { convertDate, fmtMoney } from "../../../methods/commonMethods";

// /** @typedef {import("./Sidebar").ReportKey} ReportKey */
// /** @typedef {Extract<ReportKey, `stocks-${string}`>} StockReportKey */

const PALETTE = [
  "#ffb020",
  "#4f8cff",
  "#7c5cff",
  "#21c997",
  "#ff6b6b",
  "#26c6da",
  "#ab47bc",
];

/**
 * @param {number} n
 * @returns {string}
 */
function fmtCompact(n) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

/**
 * @typedef {Object} StockReportProps
 * @property {import("../../../components/dashboard/Sidebar").ReportKey} report
 * @property {number} months
 * @property {string} store
 * @property {number} storeId
 * @property {string} category
 * @property {string} categoryId
 * @property {import("../../../lib/mockStocks").StockItem[]} stockItems
 * @property {import("../../../models/CategoryModel").CategoryModel[]} categories
 * @property {import("../../../models/StoreModel").StoreModel[]} stores
 * @property {import("../../../models/StockClosingModel").StockClosingModel[]} stockClosingEntries
 */

/**
 * @param {StockReportProps} props
 */
export function StockReport({
  report,
  months,
  store,
  storeId,
  category,
  categoryId,
  stockItems,
  categories,
  stores,
  stockClosingEntries,
}) {
  const valueSeries = useMemo(() => monthlyStockValue(months), [months]);
  const movements = useMemo(() => monthlyMovements(months), [months]);
  const adjustments = useMemo(() => monthlyAdjustments(months), [months]);
  const consumption = useMemo(() => monthlyConsumption(months), [months]);
  const topItems = useMemo(() => topConsumedItems(months), [months]);
  const alerts = useMemo(() => reorderAlerts(stockItems), [stockItems]);
  const valByCat = useMemo(() => stockValueByCategory(), []);
  const valByStore = useMemo(() => stockValueByStore(), []);

  const closing = valueSeries[valueSeries.length - 1]?.closing ?? 0;
  const prevClosing = valueSeries[valueSeries.length - 2]?.closing ?? closing;
  const mom = prevClosing
    ? Math.round(((closing - prevClosing) / prevClosing) * 1000) / 10
    : 0;
  const totalReceipts = valueSeries.reduce((s, r) => s + r.receipts, 0);
  const totalIssues = valueSeries.reduce((s, r) => s + r.issues, 0);
  const totalAdj = valueSeries.reduce((s, r) => s + Math.abs(r.adjustments), 0);
  const turnover = closing ? (totalIssues / closing).toFixed(2) : "0";

  // const filteredItems = STOCK_ITEMS.filter(
  //   (i) =>
  //     (store === "all" || i.store === store) &&
  //     (category === "all" || i.category === category),
  // );

  const [searchTerm, setSearchTerm] = useState("");

  const [currentPageItems, setCurrentPageItems] = useState(1);

  const [currentPageAlert, setCurrentPageAlert] = useState(1);

  const [currentPageStockClosingEntries, setCurrentPageStockClosingEntries] =
    useState(1);

  const filteredItems = useMemo(
    () => filterItemsMethod(stockItems, storeId, categoryId, searchTerm),
    [stockItems, categoryId, storeId, searchTerm],
  );

  const perPage = 50;

  const totalPagesItems = Math.ceil(filteredItems.length / perPage);
  const paginatedItems = filteredItems.slice(
    (currentPageItems - 1) * perPage,
    currentPageItems * perPage,
  );

  const filteredAlterts = useMemo(() => {
    // console.log(alerts);
    let l = alerts.filter(
      (item) =>
        (item.salesname || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.friendlycode || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
    // console.log(l);
    return l;
  }, [searchTerm, alerts]);

  const filteredStockClosingEntries = useMemo(() => {
    setCurrentPageStockClosingEntries(1);
    let l = stockClosingEntries.filter(
      (item) => storeId === -1 || item.storecode === Number(storeId),
    );

    return l;
  }, [stockClosingEntries, storeId]);

  const totalPagesAlerts = Math.ceil(filteredAlterts.length / perPage);
  const paginatedAlerts = filteredAlterts.slice(
    (currentPageAlert - 1) * perPage,
    currentPageAlert * perPage,
  );

  const filterNote = [
    store === "all" ? "All stores" : store,
    category === "all" ? "All categories" : category,
    `Last ${months}M`,
  ].join(" · ");

  const totalPagesStockClosingEntries = Math.ceil(
    filteredStockClosingEntries.length / perPage,
  );
  const paginatedStockClosingEntries = filteredStockClosingEntries.slice(
    (currentPageStockClosingEntries - 1) * perPage,
    currentPageStockClosingEntries * perPage,
  );

  if (report === "stocks-overview") {
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={6} xl={3}>
            <Kpi
              label="Closing Stock Value"
              value={fmtMoney(closing)}
              delta={`${mom >= 0 ? "+" : ""}${mom}% MoM`}
              icon="bi-cash-stack"
              tone="success"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Stock Turnover"
              value={`${turnover}x`}
              delta="Issues / Closing"
              icon="bi-arrow-repeat"
              tone="info"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Reorder Alerts"
              value={String(alerts.length)}
              delta={`${alerts.filter((a) => a.status === "Critical").length} critical`}
              icon="bi-exclamation-triangle-fill"
              tone="danger"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="SKUs Tracked"
              value={String((stockItems || []).length)}
              delta={filterNote}
              icon="bi-box2-fill"
              tone="warning"
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <ChartCard
              title="Stock Value Trend"
              subtitle="Monthly closing value"
            >
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={valueSeries}>
                  <defs>
                    <linearGradient id="stockGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffb020" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#ffb020" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="closing"
                    name="Closing"
                    stroke="#ffb020"
                    strokeWidth={2}
                    fill="url(#stockGrad)"
                  />
                  <Area
                    type="monotone"
                    dataKey="opening"
                    name="Opening"
                    stroke="#7c5cff"
                    strokeWidth={1.5}
                    fillOpacity={0}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={4}>
            <ChartCard
              title="Value by Category"
              subtitle="Share of on-hand value"
            >
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={valByCat}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {valByCat.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={6}>
            <ChartCard
              title="Receipts vs Issues"
              subtitle="Monthly inflow / outflow"
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={valueSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  <Bar
                    dataKey="receipts"
                    name="Receipts"
                    fill="#21c997"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="issues"
                    name="Issues"
                    fill="#ff6b6b"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={6}>
            <ChartCard title="Value by Store">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={valByStore} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis
                    type="number"
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={12}
                    width={110}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Bar dataKey="value" fill="#4f8cff" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "stocks-balance") {
    const totalValue = filteredItems.reduce(
      (s, i) => s + i.purchaseprice * i.qty,
      0,
    );
    const totalUnits = filteredItems.reduce((s, i) => s + i.qty, 0);
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Items Listed"
              value={String(filteredItems.length)}
              delta={filterNote}
              icon="bi-box2-fill"
              tone="warning"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Total On-Hand Units"
              value={totalUnits.toLocaleString()}
              icon="bi-boxes"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="On-Hand Value"
              value={fmtMoney(totalValue)}
              icon="bi-cash-stack"
              tone="success"
            />
          </Col>
        </Row>
        {/* <Row className="g-3">
          <Col xl={12}>
            <ChartCard
              title="Item Balance Report"
              subtitle="On-hand qty, cost, value and reorder status"
            > */}
        <Card className="pbi-chart-card h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div className="pbi-chart-title">Item Balance Report</div>
                <div className="pbi-chart-subtitle">{`On-hand qty, cost, value and reorder status`}</div>
              </div>
              <InputGroup size="sm" style={{ width: 340 }}>
                <InputGroup.Text>
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  className="home-search"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(v) => setSearchTerm(v.target.value)}
                />
              </InputGroup>
            </div>
            <div className="pbi-chart-body">
              <div style={{ maxHeight: 400, overflow: "auto" }}>
                <Table
                  hover
                  responsive
                  bordered
                  size="sm"
                  className="pbi-table mb-0"
                  style={{ tableLayout: "fixed", width: "100%" }}
                >
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th>Code</th>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Store</th>
                      <th>UoM</th>
                      <th className="text-end">On-Hand</th>
                      {/* <th className="text-end">Reorder</th> */}
                      <th className="text-end">Cost (Ksh)</th>
                      <th className="text-end">Value (Ksh)</th>
                      {/* <th>Re-Order Status</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {/* {(filteredItems || []).map((i, index) => { */}
                    {(paginatedItems || []).map((i, index) => {
                      // const status =
                      //   i.qty < i.minstock * 0.5
                      //     ? { label: "Critical", bg: "danger" }
                      //     : i.qty < i.minstock
                      //       ? { label: "Low", bg: "warning" }
                      //       : { label: "OK", bg: "success" };
                      // const status2 =
                      //   i.total_item_qty < i.minstock * 0.5
                      //     ? { label: "Critical", bg: "danger" }
                      //     : i.total_item_qty < i.minstock
                      //       ? { label: "Low", bg: "warning" }
                      //       : { label: "OK", bg: "success" };
                      return (
                        <>
                          <tr key={index}>
                            <td className="text-muted">{i.friendlycode}</td>
                            <td className="fw-semibold">{i.salesname}</td>
                            <td>
                              {(categories || []).find(
                                (c) => Number(c.id) === i.maincatcode,
                              )?.name || "UnKnown"}
                            </td>
                            <td>
                              {(stores || []).find(
                                (s) => s.storecode === i.storecode,
                              )?.storename || "UnKnown"}
                            </td>
                            <td>{i.unitmeasure}</td>
                            <td className="text-end">
                              {(i.qty || 0).toLocaleString()}
                            </td>
                            {/* <td className="text-end text-muted">
                                {i.minstock}
                              </td> */}
                            <td className="text-end">
                              {fmtMoney(i.purchaseprice || 0, false)}
                            </td>
                            <td className="text-end fw-semibold">
                              {fmtMoney(
                                (i.purchaseprice || 0) * (i.qty || 0),
                                false,
                              )}
                            </td>
                            {/* <td>
                                <Badge bg={status.bg}>{status.label}</Badge>
                              </td> */}
                          </tr>
                          {
                            // index !== 0 &&
                            storeId === -1 &&
                              (index === paginatedItems.length - 1 ||
                                i.itemcode !==
                                  paginatedItems[index + 1]?.itemcode) && (
                                <tr
                                  key={`${index}t`}
                                  className="item_total_row"
                                >
                                  <td className="text-muted"></td>
                                  <td className="fw-semibold"></td>
                                  <td></td>
                                  <td>Total</td>
                                  <td>{i.unitmeasure}</td>
                                  <td className="text-end">
                                    {(i.total_item_qty || 0).toLocaleString()}
                                  </td>
                                  {/* <td className="text-end text-muted">
                                      {i.minstock}
                                    </td> */}
                                  <td className="text-end">
                                    {fmtMoney(i.purchaseprice || 0, false)}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {fmtMoney(
                                      (i.purchaseprice || 0) *
                                        (i.total_item_qty || 0),
                                      false,
                                    )}
                                  </td>
                                  {/* <td>
                                      <Badge bg={status2.bg}>
                                        {status2.label}
                                      </Badge>
                                    </td> */}
                                </tr>
                              )
                          }
                        </>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <Pagination className="mt-2">
                <Pagination.Prev
                  disabled={currentPageItems === 1}
                  onClick={() => setCurrentPageItems((p) => p - 1)}
                />
                <Pagination.Item>
                  {currentPageItems} / {totalPagesItems}
                </Pagination.Item>

                <Pagination.Next
                  disabled={currentPageItems === totalPagesItems}
                  onClick={() => setCurrentPageItems((p) => p + 1)}
                />
              </Pagination>
            </div>
          </Card.Body>
        </Card>
        {/* </ChartCard>
          </Col>
        </Row> */}
      </>
    );
  }

  if (report === "stocks-movement") {
    const totalPurch = movements.reduce((s, r) => s + r.purchases, 0);
    const totalSales = movements.reduce((s, r) => s + r.sales, 0);
    const totalTransfers = movements.reduce(
      (s, r) => s + r.transfersIn + r.transfersOut,
      0,
    );
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Kpi
              label="Purchases"
              value={fmtMoney(totalPurch)}
              icon="bi-truck"
              tone="info"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Sales Issues"
              value={fmtMoney(totalSales)}
              icon="bi-arrow-up-right-circle"
              tone="success"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Transfers"
              value={fmtMoney(totalTransfers)}
              icon="bi-arrow-left-right"
              tone="primary"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Returns"
              value={fmtMoney(movements.reduce((s, r) => s + r.returns, 0))}
              icon="bi-arrow-counterclockwise"
              tone="warning"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={12}>
            <ChartCard
              title="Stock Movement Trend"
              subtitle="Purchases, transfers, sales and returns per month"
            >
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={movements}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  <Bar
                    dataKey="purchases"
                    name="Purchases"
                    stackId="in"
                    fill="#4f8cff"
                  />
                  <Bar
                    dataKey="transfersIn"
                    name="Transfers In"
                    stackId="in"
                    fill="#26c6da"
                  />
                  <Bar
                    dataKey="sales"
                    name="Sales"
                    stackId="out"
                    fill="#ff6b6b"
                  />
                  <Bar
                    dataKey="transfersOut"
                    name="Transfers Out"
                    stackId="out"
                    fill="#ffb020"
                  />
                  <Line
                    type="monotone"
                    dataKey="returns"
                    name="Returns"
                    stroke="#7c5cff"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={12}>
            <ChartCard title="Movements Ledger" subtitle="Monthly summary">
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Purchases</th>
                    <th className="text-end">Transfers In</th>
                    <th className="text-end">Sales</th>
                    <th className="text-end">Transfers Out</th>
                    <th className="text-end">Returns</th>
                    <th className="text-end">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((r) => {
                    const net =
                      r.purchases +
                      r.transfersIn +
                      r.returns -
                      r.sales -
                      r.transfersOut;
                    return (
                      <tr key={r.month}>
                        <td className="fw-semibold">{r.month}</td>
                        <td className="text-end">{fmtMoney(r.purchases)}</td>
                        <td className="text-end">{fmtMoney(r.transfersIn)}</td>
                        <td className="text-end">{fmtMoney(r.sales)}</td>
                        <td className="text-end">{fmtMoney(r.transfersOut)}</td>
                        <td className="text-end">{fmtMoney(r.returns)}</td>
                        <td
                          className={`text-end fw-semibold ${net >= 0 ? "text-success" : "text-danger"}`}
                        >
                          {fmtMoney(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "stocks-adjustment") {
    const totals = ADJUSTMENT_REASONS.map((r) => ({
      name: r,
      value: adjustments.reduce((s, m) => s + /** @type {number} */ (m[r]), 0),
    }));
    const grand = totals.reduce((s, r) => s + r.value, 0);
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Total Adjustments"
              value={fmtMoney(grand)}
              delta={filterNote}
              icon="bi-sliders"
              tone="warning"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Top Reason"
              value={totals.sort((a, b) => b.value - a.value)[0].name}
              delta={fmtMoney(totals[0].value)}
              icon="bi-exclamation-diamond-fill"
              tone="danger"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Reasons Tracked"
              value={String(ADJUSTMENT_REASONS.length)}
              icon="bi-list-check"
              tone="info"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={8}>
            <ChartCard title="Adjustments by Reason" subtitle="Stacked monthly">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={adjustments}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  {ADJUSTMENT_REASONS.map((r, i) => (
                    <Bar
                      key={r}
                      dataKey={r}
                      stackId="a"
                      fill={PALETTE[i % PALETTE.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={4}>
            <ChartCard title="Reason Mix">
              <ResponsiveContainer width="100%" height={340}>
                <PieChart>
                  <Pie
                    data={totals}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={120}
                    label={(e) => `${Math.round((e.value / grand) * 100)}%`}
                  >
                    {totals.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "stocks-consumption") {
    const totalCons = consumption.reduce(
      (s, r) =>
        s +
        STOCK_CATEGORIES.reduce((a, c) => a + /** @type {number} */ (r[c]), 0),
      0,
    );
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Total Consumption"
              value={fmtMoney(totalCons)}
              delta={filterNote}
              icon="bi-basket-fill"
              tone="danger"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Top Consumed"
              value={topItems[0].name}
              delta={fmtMoney(topItems[0].value)}
              icon="bi-trophy-fill"
              tone="warning"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Avg Monthly"
              value={fmtMoney(Math.round(totalCons / months))}
              icon="bi-calendar-month"
              tone="info"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={12}>
            <ChartCard
              title="Consumption by Category"
              subtitle="Monthly issues to service / production"
            >
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={consumption}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  {STOCK_CATEGORIES.map((c, i) => (
                    <Line
                      key={c}
                      type="monotone"
                      dataKey={c}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={12}>
            <ChartCard
              title="Top Consumed Items"
              subtitle="Ranked by cost of goods issued"
            >
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Item</th>
                    <th>Category</th>
                    <th className="text-end">Qty Issued</th>
                    <th className="text-end">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {topItems.map((t, i) => (
                    <tr key={t.code}>
                      <td className="text-muted">{i + 1}</td>
                      <td className="text-muted">{t.code}</td>
                      <td className="fw-semibold">{t.name}</td>
                      <td>{t.category}</td>
                      <td className="text-end">{t.qty.toLocaleString()}</td>
                      <td className="text-end fw-semibold">
                        {fmtMoney(t.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "stocks-reorder") {
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Alerts"
              value={String(filteredAlterts.length)}
              delta={filterNote}
              icon="bi-bell-fill"
              tone="warning"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Critical"
              value={String(
                filteredAlterts.filter((a) => a.status === "Critical").length,
              )}
              icon="bi-exclamation-octagon-fill"
              tone="danger"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Low"
              value={String(
                filteredAlterts.filter((a) => a.status === "Low").length,
              )}
              icon="bi-exclamation-triangle-fill"
              tone="info"
            />
          </Col>
        </Row>
        {/* <Row className="g-3">
          <Col xl={12}>
            <ChartCard
              title="Reorder Alerts"
              subtitle="Items at or below reorder level"
            > */}
        <Card className="pbi-chart-card h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div className="pbi-chart-title">Item Balance Report</div>
                <div className="pbi-chart-subtitle">{`On-hand qty, cost, value and reorder status`}</div>
              </div>
              <InputGroup size="sm" style={{ width: 340 }}>
                <InputGroup.Text>
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  className="home-search"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(v) => setSearchTerm(v.target.value)}
                />
              </InputGroup>
            </div>
            <div className="pbi-chart-body">
              <div style={{ maxHeight: 400, overflow: "auto" }}>
                <Table
                  hover
                  responsive
                  striped
                  bordered
                  size="sm"
                  className="pbi-table mb-0"
                >
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th>Code</th>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Store</th>
                      <th className="text-end">On-Hand</th>
                      <th className="text-end">Reorder</th>
                      <th className="text-end">Shortfall</th>
                      <th style={{ width: 220 }}>Level</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* {filteredAlterts.map((a, index) => { */}
                    {paginatedAlerts.map((a, index) => {
                      const pct = Math.min(
                        100,
                        Math.round((a.qty / a.minstock) * 100),
                      );
                      const variant =
                        a.status === "Critical"
                          ? "danger"
                          : a.status === "Low"
                            ? "warning"
                            : "info";
                      return (
                        <tr key={index}>
                          <td className="text-muted">{a.friendlycode}</td>
                          <td className="fw-semibold">{a.salesname}</td>
                          <td>
                            {(categories || []).find(
                              (c) => Number(c.id) === a.maincatcode,
                            )?.name || "UnKnown"}
                          </td>
                          <td>
                            {(stores || []).find(
                              (s) => s.storecode === a.storecode,
                            )?.storename || "UnKnown"}
                          </td>
                          <td className="text-end">{a.qty}</td>
                          <td className="text-end text-muted">{a.minstock}</td>
                          <td className="text-end">{a.shortfall}</td>
                          <td>
                            <ProgressBar
                              now={pct}
                              variant={variant}
                              style={{ height: 8 }}
                            />
                          </td>
                          <td>
                            <Badge bg={variant}>{a.status}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <Pagination className="mt-2">
                <Pagination.Prev
                  disabled={currentPageAlert === 1}
                  onClick={() => setCurrentPageAlert((p) => p - 1)}
                />
                <Pagination.Item>
                  {currentPageAlert} / {totalPagesAlerts}
                </Pagination.Item>

                <Pagination.Next
                  disabled={currentPageAlert === totalPagesAlerts}
                  onClick={() => setCurrentPageAlert((p) => p + 1)}
                />
              </Pagination>
            </div>
          </Card.Body>
        </Card>
        {/* </ChartCard>
          </Col>
        </Row> */}
      </>
    );
  }

  if (report === "stocks-valuation") {
    const totalValue = valByCat.reduce((s, r) => s + r.value, 0);
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Kpi
              label="On-Hand Value"
              value={fmtMoney(totalValue)}
              icon="bi-cash-stack"
              tone="success"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Receipts (period)"
              value={fmtMoney(totalReceipts)}
              icon="bi-truck"
              tone="info"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Issues (period)"
              value={fmtMoney(totalIssues)}
              icon="bi-arrow-up-right-circle"
              tone="warning"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Adjustments"
              value={fmtMoney(totalAdj)}
              icon="bi-sliders"
              tone="danger"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={7}>
            <ChartCard
              title="Opening → Closing"
              subtitle="Value bridge per month"
            >
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={valueSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  <Bar
                    dataKey="opening"
                    name="Opening"
                    fill="#c9d1e0"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="closing"
                    name="Closing"
                    fill="#ffb020"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="adjustments"
                    name="Adjustments"
                    stroke="#ff6b6b"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Category Valuation">
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-end">SKUs</th>
                    <th className="text-end">Value (Ksh)</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {valByCat.map((c) => (
                    <tr key={c.name}>
                      <td className="fw-semibold">{c.name}</td>
                      <td className="text-end">{c.items}</td>
                      <td className="text-end">{fmtMoney(c.value, false)}</td>
                      <td className="text-end">
                        {Math.round((c.value / totalValue) * 100)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }
  if (report === "stocks-closing") {
    return (
      <>
        <Card className="pbi-chart-card h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div className="pbi-chart-title">
                  View Stock Closing Entries
                </div>
                <div className="pbi-chart-subtitle"></div>
              </div>
            </div>
            <div className="pbi-chart-body">
              <div style={{ maxHeight: 400, overflow: "auto" }}>
                <Table
                  hover
                  responsive
                  striped
                  bordered
                  size="sm"
                  className="pbi-table mb-0"
                >
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th>VCHNo</th>
                      <th>Store Name</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Agent Name</th>
                      <th className="text-end">% Profit</th>
                      <th className="text-end">Sales</th>
                      <th className="text-end">Cost of Sales</th>
                      <th className="text-end">Trans Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* {filteredAlterts.map((a, index) => { */}
                    {paginatedStockClosingEntries.map((a, index) => {
                      return (
                        <tr key={index}>
                          <td className="text-muted">{a.vchno}</td>
                          <td className="fw-semibold">
                            {(stores || []).find(
                              (s) => s.storecode === a.storecode,
                            )?.storename || "UnKnown"}
                          </td>
                          <td className="text-muted">
                            {convertDate(`${a.sdatefrom}T${a.stimefrom}`, true)}
                          </td>
                          <td className="text-muted">
                            {convertDate(`${a.sdate}T${a.stime}`, true)}
                          </td>
                          <td className="text-muted">{a.agentname}</td>
                          <td className="text-end">
                            {fmtMoney(a.percgrossprofit, false)}
                          </td>
                          <td className="text-end text-muted">
                            {fmtMoney(a.totalsales, false)}
                          </td>
                          <td className="text-end">
                            {fmtMoney(a.cashamount, false)}
                          </td>
                          <td>{a.transtypename}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <Pagination className="mt-2">
                <Pagination.Prev
                  disabled={currentPageStockClosingEntries === 1}
                  onClick={() =>
                    setCurrentPageStockClosingEntries((p) => p - 1)
                  }
                />
                <Pagination.Item>
                  {currentPageStockClosingEntries} /{" "}
                  {totalPagesStockClosingEntries}
                </Pagination.Item>

                <Pagination.Next
                  disabled={
                    currentPageStockClosingEntries ===
                    totalPagesStockClosingEntries
                  }
                  onClick={() =>
                    setCurrentPageStockClosingEntries((p) => p + 1)
                  }
                />
              </Pagination>
            </div>
          </Card.Body>
        </Card>
      </>
    );
  }

  return <div className="text-muted">Report not found.</div>;
}
